<template>
  <div class="technical-decision-panel">
    <!-- Botões de decisão -->
    <div v-if="loadingDecisions" class="text-center py-4">
      <b-spinner variant="primary" />
    </div>

    <div v-else-if="!decisions.length" class="text-center text-muted py-4">
      <feather-icon icon="ZapOffIcon" size="32" class="mb-2" />
      <p class="mb-0">Nenhuma decisão técnica cadastrada para esta disciplina.</p>
    </div>

    <div v-else-if="$hasAbility('regulacao.gerenciar')" class="decision-grid mb-4">
      <button
        v-for="decision in decisions"
        :key="decision.id"
        class="decision-btn"
        :class="decisionClass(decision.fixed_key)"
        @click="openDecisionModal(decision)"
      >
        <feather-icon :icon="decisionIcon(decision.fixed_key)" size="22" class="mb-50" />
        <span>{{ decision.name }}</span>
      </button>
    </div>

    <!-- Histórico de decisões -->
    <div class="card">
      <div class="card-header py-2 d-flex justify-content-between align-items-center">
        <h6 class="mb-0">
          <feather-icon icon="ListIcon" size="15" class="mr-50" />
          Histórico de Decisões
        </h6>
        <button class="btn btn-sm btn-outline-secondary" @click="fetchLogs">
          <feather-icon icon="RefreshCwIcon" size="13" />
        </button>
      </div>
      <div class="card-body p-0">
        <div v-if="loadingLogs" class="text-center py-3">
          <b-spinner small variant="primary" />
        </div>
        <div v-else-if="!logs.length" class="text-center text-muted py-3" style="font-size: 13px">
          Nenhuma decisão registrada até o momento.
        </div>
        <div v-else class="dec-log-list">
          <div
            v-for="log in logs"
            :key="log.id"
            class="dec-log-item"
            :class="logItemClass(log)"
          >
            <div class="dec-log-item__icon">
              <feather-icon
                :icon="decisionIcon(log.technical_decision ? log.technical_decision.fixed_key : null)"
                size="15"
              />
            </div>
            <div class="dec-log-item__body">
              <div class="dec-log-item__name">
                {{ log.technical_decision ? log.technical_decision.name : '—' }}
                <span
                  v-if="log.team_request"
                  class="dec-log-item__state"
                  :class="'dec-log-item__state--' + requestStateKey(log.team_request.status)"
                >{{ requestStateLabel(log.team_request.status) }}</span>
              </div>
              <div v-if="log.details" class="dec-log-item__details">
                {{ log.details }}
              </div>
              <div class="dec-log-item__meta">
                <feather-icon icon="UserIcon" size="10" class="mr-25" />
                {{ log.user ? log.user.name : '—' }}
                <span class="dec-log-item__sep">·</span>
                {{ log.created_at | formatDate }}
              </div>
            </div>

            <button
              v-if="canCancelRequest(log)"
              class="btn btn-sm btn-outline-danger dec-log-item__action"
              @click="$emit('cancel-request', log.team_request)"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal genérico / orientação / apoio -->
    <b-modal
      v-model="showGenericModal"
      :title="activeDecision ? activeDecision.name : ''"
      ok-title="Registrar"
      cancel-title="Cancelar"
      ok-variant="primary"
      :ok-disabled="savingDecision"
      @ok="confirmDecision"
    >
      <div class="form-group">
        <label>Detalhes / Observação:</label>
        <textarea
          class="form-control"
          rows="4"
          v-model="decisionDetails"
          placeholder="Descreva a decisão tomada..."
          maxlength="2000"
        />
      </div>
    </b-modal>

    <!-- Modal solicitação de veículo -->
    <b-modal
      v-model="showVehicleModal"
      :title="activeDecision ? activeDecision.name : 'Solicitação de equipe'"
      ok-title="Solicitar"
      cancel-title="Cancelar"
      ok-variant="primary"
      :ok-disabled="savingDecision || !vehicleRequest.team_type_id"
      @ok="confirmVehicleRequest"
    >
      <div v-if="pendingRequests.length" class="tr-context">
        <span class="tr-context__label">Já pendente nesta ocorrência</span>
        <span
          v-for="req in pendingRequests"
          :key="req.id"
          class="tr-context__item"
        >
          {{ req.team_type ? req.team_type.name : '—' }}
          <em>{{ req.created_at | shortTime }}</em>
        </span>
      </div>

      <div class="form-group">
        <label><span class="required">*</span> Tipo de Equipe:</label>

        <div v-if="loadingTeamTypes" class="tt-loading">
          <b-spinner small class="mr-50" /> Consultando disponibilidade...
        </div>

        <div v-else-if="!teamTypes.length" class="tt-alert tt-alert--warn">
          <feather-icon icon="AlertCircleIcon" size="14" class="mr-50" />
          <span>Nenhum tipo de equipe cadastrado neste nó.</span>
        </div>

        <div v-else class="tt-grid" role="radiogroup" aria-label="Tipo de equipe">
          <label
            v-for="t in teamTypes"
            :key="t.id"
            class="tt-card"
            :class="{
              'tt-card--on': vehicleRequest.team_type_id === t.id,
              'tt-card--out': t.blocked,
            }"
          >
            <input
              v-model="vehicleRequest.team_type_id"
              type="radio"
              name="team-type"
              class="tt-card__input"
              :value="t.id"
            >
            <span class="tt-card__name">{{ t.name }}</span>
            <span class="tt-card__avail">
              <feather-icon
                :icon="t.blocked ? 'SlashIcon' : 'CheckCircleIcon'"
                size="11"
                class="mr-25"
              />{{ t.blocked
                   ? 'nenhuma livre'
                   : t.available + ' de ' + t.total + (t.available > 1 ? ' livres' : ' livre') }}
            </span>
          </label>
        </div>

        <small v-if="loadingTeamTypes" class="text-muted">Consultando disponibilidade...</small>

        <div v-else-if="allTypesBlocked" class="tt-alert tt-alert--crit">
          <feather-icon icon="AlertTriangleIcon" size="14" class="mr-50" />
          <span>
            Nenhuma equipe disponível para despacho neste momento. Uma equipe conta
            como disponível quando está embarcada, em posição que permite despacho e
            sem despacho ativo.
          </span>
        </div>

        <div v-else-if="untypedTeams > 0" class="tt-alert tt-alert--warn">
          <feather-icon icon="AlertCircleIcon" size="14" class="mr-50" />
          <span>
            {{ untypedTeams }} equipe(s) sem tipo definido não entram nesta contagem.
            Defina o tipo no cadastro de equipes.
          </span>
        </div>

        <div v-if="selectedTypeBlocked" class="tt-alert tt-alert--warn mt-50">
          <feather-icon icon="AlertTriangleIcon" size="14" class="mr-50" />
          <span>
            Não há unidade deste tipo livre agora. A solicitação fica pendente no
            painel de despacho até que uma seja despachada.
          </span>
        </div>

        <div v-if="pendingSameType.length" class="tt-alert tt-alert--info mt-50">
          <feather-icon icon="InfoIcon" size="14" class="mr-50" />
          <span>
            Já há <strong>{{ pendingSameTypeQty }} {{ selectedTypeName }}</strong>
            pendente nesta ocorrência<template v-if="lastPendingSameType">, desde
            {{ lastPendingSameType.created_at | shortTime }}</template>.
            Confirmar pede a <strong>{{ pendingSameTypeQty + 1 }}ª unidade</strong>.
          </span>
        </div>
      </div>
      <div class="form-group">
        <label>Código / Observação:</label>
        <input
          class="form-control"
          type="text"
          v-model="vehicleRequest.code"
          placeholder="Ex: SAMU 192, código de chamada..."
        />
      </div>
    </b-modal>

    <BusinessRulePendenciesModal
      :payload="rulePendencies"
      :retrying="ruleRetrying"
      title="Não é possível solicitar a equipe"
      @retry="executeGuarded"
      @close="closeRulePendencies"
      @open="handleOpenPendency"
    />

    <!-- Modal telemedicina -->
    <b-modal
      v-model="showTelemedicineModal"
      title="Telemedicina"
      ok-title="Registrar"
      cancel-title="Cancelar"
      ok-variant="primary"
      :ok-disabled="savingDecision"
      @ok="confirmTelemedicine"
    >
      <div class="form-group">
        <label>Especialidade / Contato:</label>
        <input class="form-control" type="text" v-model="telemedicineDetails.contact" placeholder="Ex: Cardiologia — Dr. João" />
      </div>
      <div class="form-group">
        <label>Observações:</label>
        <textarea class="form-control" rows="3" v-model="telemedicineDetails.notes" placeholder="Descreva o encaminhamento..." maxlength="2000" />
      </div>
    </b-modal>
  </div>
</template>

<script>
import ApiErrorMixin from '@/mixins/ApiErrorMixin'
import BusinessRuleGuardMixin from '@/mixins/BusinessRuleGuardMixin'
import BusinessRulePendenciesModal from '@/components/BusinessRulePendenciesModal.vue'

export default {
  name: 'TechnicalDecisionPanel',
  components: { BusinessRulePendenciesModal },
  mixins: [ApiErrorMixin, BusinessRuleGuardMixin],
  filters: {
    shortTime(val) {
      if (!val) return ''
      return new Date(val).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    },
    formatDate(val) {
      if (!val) return '—'
      const d = new Date(val)
      return d.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      })
    },
  },
  props: {
    occurrenceId: { type: [Number, String], required: true },
    disciplineId: { type: [Number, String], default: null },
    pendingRequests: { type: Array, default: () => [] },
  },
  data() {
    return {
      decisions: [],
      loadingDecisions: false,
      logs: [],
      loadingLogs: false,

      activeDecision: null,
      decisionDetails: '',
      savingDecision: false,

      showGenericModal: false,
      showVehicleModal: false,
      showTelemedicineModal: false,

      teamTypes: [],
      loadingTeamTypes: false,
      untypedTeams: 0,
      vehicleRequest: { team_type_id: null, code: '' },

      telemedicineDetails: { contact: '', notes: '' },
    }
  },
  computed: {
    allTypesBlocked() {
      return this.teamTypes.length > 0 && this.teamTypes.every(t => t.blocked)
    },

    selectedTypeBlocked() {
      const t = this.teamTypes.find(x => x.id === this.vehicleRequest.team_type_id)
      return !!(t && t.blocked)
    },

    selectedTypeName() {
      const t = this.teamTypes.find(x => x.id === this.vehicleRequest.team_type_id)
      return t ? t.name : ''
    },

    pendingSameType() {
      const id = this.vehicleRequest.team_type_id
      if (!id) return []
      return this.pendingRequests.filter(r => r.team_type_id === id)
    },

    pendingSameTypeQty() {
      return this.pendingSameType.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0)
    },

    lastPendingSameType() {
      return this.pendingSameType[this.pendingSameType.length - 1] || null
    },
  },

  mounted() {
    this.fetchDecisions()
    this.fetchLogs()
  },
  methods: {
    async fetchDecisions() {
      this.loadingDecisions = true
      try {
        const params = { status: 1 }
        if (this.disciplineId) params.discipline_id = this.disciplineId
        const res = await this.$http.get(this.$store.state.api + 'technical_decisions', { params })
        this.decisions = res.data.data || res.data || []
      } catch (e) {
        this.decisions = []
      } finally {
        this.loadingDecisions = false
      }
    },
    async fetchLogs() {
      this.loadingLogs = true
      try {
        const res = await this.$http.get(this.$store.state.api + 'occurrence_technical_decision_logs', {
          params: { occurrence_id: this.occurrenceId },
        })
        this.logs = res.data.data || res.data || []
      } catch (e) {
        this.logs = []
      } finally {
        this.loadingLogs = false
      }
    },
    openDecisionModal(decision) {
      this.activeDecision = decision
      this.decisionDetails = ''
      this.vehicleRequest = { team_type_id: null, code: '' }
      this.telemedicineDetails = { contact: '', notes: '' }

      const TEAM_REQUEST_KEYS = ['team_request', 'vehicle_request']
      const TELEMEDICINE_KEYS = ['teleservice', 'telemedicine']

      if (TEAM_REQUEST_KEYS.includes(decision.fixed_key)) {
        this.teamTypes = []
        this.loadTeamTypes()
        this.showVehicleModal = true
      } else if (TELEMEDICINE_KEYS.includes(decision.fixed_key)) {
        this.showTelemedicineModal = true
      } else {
        this.showGenericModal = true
      }
    },
    // Sem cache de propósito: disponibilidade muda a cada embarque, despacho e
    // mudança de posição. Reaproveitar a lista da abertura anterior ofereceria
    // um tipo que acabou de ficar sem unidade livre.
    async loadTeamTypes() {
      this.loadingTeamTypes = true
      try {
        const res = await this.$http.get(this.$store.state.api + 'team_types/availability')
        this.teamTypes = res.data.data || []
        this.untypedTeams = res.data.meta ? res.data.meta.untyped_teams : 0
      } catch (e) {
        this.teamTypes = []
        this.untypedTeams = 0
        this.$message('Erro', this.apiErrorText(e, 'Não foi possível consultar a disponibilidade de equipes.'), 'error')
      } finally {
        this.loadingTeamTypes = false
      }
    },
    async confirmDecision(bvModalEvent) {
      bvModalEvent.preventDefault()
      await this.persistLog(this.decisionDetails || null)
    },
    async confirmVehicleRequest(bvModalEvent) {
      bvModalEvent.preventDefault()

      if (!this.vehicleRequest.team_type_id) {
        this.$message('Atenção', 'Selecione o tipo de equipe.', 'warning')
        return
      }

      this.savingDecision = true
      try {
        await this.runGuarded(
          options => this.$http.post(`${this.$store.state.api}occurrence_team_requests`, {
            occurrence_id: this.occurrenceId,
            team_type_id: this.vehicleRequest.team_type_id,
            technical_decision_id: this.activeDecision.id,
            notes: this.vehicleRequest.code || null,
            ...options,
          }),
          res => {
            this.showVehicleModal = false
            this.activeDecision = null
            this.fetchLogs()
            this.$emit('team-requested', res.data.data)
            this.$message('Regulação', 'Solicitação de equipe registrada.', 'success')
          },
        )
      } catch (e) {
        this.$message('Erro', this.apiErrorText(e, 'Erro ao registrar a solicitação de equipe.'), 'error')
      } finally {
        this.savingDecision = false
      }
    },

    /** Fecha o modal de solicitação e delega a navegação até o painel pendente ao pai. */
    handleOpenPendency(violation) {
      this.closeRulePendencies()
      this.showVehicleModal = false
      this.$emit('open-pendency', violation)
    },
    async confirmTelemedicine(bvModalEvent) {
      bvModalEvent.preventDefault()
      const details = [
        this.telemedicineDetails.contact ? 'Contato: ' + this.telemedicineDetails.contact : null,
        this.telemedicineDetails.notes || null,
      ].filter(Boolean).join(' — ')
      await this.persistLog(details || null)
    },
    async persistLog(details) {
      this.savingDecision = true
      try {
        const res = await this.$http.post(this.$store.state.api + 'occurrence_technical_decision_logs', {
          occurrence_id: this.occurrenceId,
          technical_decision_id: this.activeDecision.id,
          details,
        })
        const saved = res.data.data || res.data
        this.logs.unshift(saved)
        this.showGenericModal = false
        this.showVehicleModal = false
        this.showTelemedicineModal = false
        this.activeDecision = null
      } catch (e) {
        this.$message('Erro', this.apiErrorText(e, 'Erro ao registrar decisão.'), 'error')
      } finally {
        this.savingDecision = false
      }
    },
    canCancelRequest(log) {
      return !!log.team_request
        && log.team_request.status === 1
        && this.$hasAbility('regulacao.gerenciar')
    },

    requestStateKey(status) {
      return { 1: 'pend', 2: 'ok', 3: 'cancel' }[status] || 'pend'
    },

    requestStateLabel(status) {
      return { 1: 'pendente', 2: 'atendida', 3: 'cancelada' }[status] || 'pendente'
    },

    decisionIcon(fixedKey) {
      const icons = {
        medical_guidance: 'BookOpenIcon',
        vehicle_request: 'TruckIcon',
        team_request: 'TruckIcon',
        support_request: 'PhoneCallIcon',
        telemedicine: 'MonitorIcon',
        teleservice: 'MonitorIcon',
      }
      return icons[fixedKey] || 'ZapIcon'
    },
    decisionClass(fixedKey) {
      const classes = {
        medical_guidance: 'decision-btn--blue',
        vehicle_request: 'decision-btn--green',
        team_request: 'decision-btn--green',
        support_request: 'decision-btn--orange',
        telemedicine: 'decision-btn--purple',
        teleservice: 'decision-btn--purple',
      }
      return classes[fixedKey] || 'decision-btn--default'
    },
    logItemClass(log) {
      const key = log.technical_decision ? log.technical_decision.fixed_key : null
      const map = {
        medical_guidance: 'dec-log-item--blue',
        vehicle_request: 'dec-log-item--green',
        team_request: 'dec-log-item--green',
        support_request: 'dec-log-item--orange',
        telemedicine: 'dec-log-item--purple',
        teleservice: 'dec-log-item--purple',
      }
      return map[key] || 'dec-log-item--default'
    },
  },
}
</script>

<style scoped>
.decision-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.decision-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 12px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  min-height: 90px;
  text-align: center;
  gap: 4px;
}

.decision-btn:hover {
  opacity: 0.88;
  transform: translateY(-1px);
}

.decision-btn--blue   { background: rgba(37, 99, 235, 0.15); border-color: #2563eb; color: #60a5fa; }
.decision-btn--green  { background: rgba(22, 163, 74, 0.15); border-color: #16a34a; color: #4ade80; }
.decision-btn--orange { background: rgba(234, 88, 12, 0.15); border-color: #ea580c; color: #fb923c; }
.decision-btn--purple { background: rgba(124, 58, 237, 0.15); border-color: #7c3aed; color: #a78bfa; }
.decision-btn--default { background: rgba(107, 114, 128, 0.15); border-color: #6b7280; color: #9ca3af; }

/* ── Histórico de decisões ── */
.dec-log-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
}

.dec-log-item {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 11px 13px;
  border: 1px solid #ebe9f1;
  border-left: 4px solid #6b7280;
  border-radius: 6px;
  background: #fafafa;
  transition: background 0.15s;
}

.dec-log-item--blue   { border-left-color: #2563eb; background: rgba(37,99,235,0.04); }
.dec-log-item--blue   .dec-log-item__icon { color: #2563eb; }

.dec-log-item--green  { border-left-color: #16a34a; background: rgba(22,163,74,0.04); }
.dec-log-item--green  .dec-log-item__icon { color: #16a34a; }

.dec-log-item--orange { border-left-color: #ea580c; background: rgba(234,88,12,0.04); }
.dec-log-item--orange .dec-log-item__icon { color: #ea580c; }

.dec-log-item--purple { border-left-color: #7c3aed; background: rgba(124,58,237,0.04); }
.dec-log-item--purple .dec-log-item__icon { color: #7c3aed; }

.dec-log-item--default .dec-log-item__icon { color: #6b7280; }

.dec-log-item__icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.dec-log-item__body {
  flex: 1;
  min-width: 0;
}

.dec-log-item__name {
  font-size: 13px;
  font-weight: 700;
  color: #5e5873;
  margin-bottom: 4px;
  line-height: 1.3;
}

.dec-log-item__details {
  font-size: 12px;
  color: #6e6b7b;
  margin-bottom: 5px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.dec-log-item__meta {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #b9b9c3;
  flex-wrap: wrap;
  gap: 2px;
}

.dec-log-item__sep {
  margin: 0 2px;
}

/* dark */
.dark-layout .dec-log-item {
  background: #1c2333;
  border-color: #3b4253;
}

.dark-layout .dec-log-item--blue   { background: rgba(37,99,235,0.08); }
.dark-layout .dec-log-item--green  { background: rgba(22,163,74,0.08); }
.dark-layout .dec-log-item--orange { background: rgba(234,88,12,0.08); }
.dark-layout .dec-log-item--purple { background: rgba(124,58,237,0.08); }

.dark-layout .dec-log-item__name {
  color: #d0d2d6;
}

.dark-layout .dec-log-item__details {
  color: #a0a0b0;
}

/* Disponibilidade por tipo de equipe (solicitação de viatura) */
.tt-opt {
  width: 100%;
  gap: 8px;
}
.tt-opt--blocked {
  opacity: 0.55;
}
.tt-name {
  font-weight: 600;
}
.tt-count {
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
}
.tt-count--ok {
  background: #e8f7ee;
  color: #15803d;
}
.tt-count--none {
  background: #f1f2f4;
  color: #6b7280;
}

.tt-alert {
  display: flex;
  align-items: flex-start;
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  line-height: 1.45;
}
.tt-alert--crit {
  background: #fdeaea;
  color: #b3261e;
}
.tt-alert--warn {
  background: #fbf2e0;
  color: #8a5300;
}

.dark-layout .tt-count--ok   { background: #14301f; color: #4ade80; }
.dark-layout .tt-count--none { background: #2c3247; color: #a0a3ad; }
.dark-layout .tt-alert--crit { background: #3a1c1c; color: #f08b84; }
.dark-layout .tt-alert--warn { background: #33290f; color: #e0a641; }

.tr-context {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  padding: 9px 11px;
  border-radius: 6px;
  background: #f6f6f9;
  font-size: 12px;
}
.tr-context__label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #b9b9c3;
  margin-right: 2px;
}
.tr-context__item {
  padding: 2px 8px;
  border-radius: 10px;
  background: #fff3e6;
  color: #ff9f43;
  font-weight: 600;
}
.tr-context__item em { font-style: normal; font-weight: 400; opacity: 0.8; margin-left: 4px; }

.tt-alert--dup {
  background: #fff3e6;
  color: #b06a1f;
}
.tt-alert--dup strong { color: #8f5115; }

.dark-layout .tr-context { background: #1c2333; }
.dark-layout .tr-context__label { color: #676d7d; }
.dark-layout .tr-context__item { background: #3d2f1c; }
.dark-layout .tt-alert--dup { background: #3d2f1c; color: #ffbb7a; }
.dark-layout .tt-alert--dup strong { color: #ffd0a3; }

.tt-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 8px;
}

.tt-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin: 0;
  padding: 11px 12px;
  border: 1.5px solid #ebe9f1;
  border-radius: 7px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;
}
.tt-card:hover { border-color: #c3bed4; }

.tt-card__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
}
.tt-card__input:focus-visible ~ .tt-card__name { text-decoration: underline; }

.tt-card__name {
  font-size: 14px;
  font-weight: 600;
  color: #5e5873;
  line-height: 1.2;
}
.tt-card__avail {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: #28c76f;
}

.tt-card--on {
  border-color: #7367f0;
  background: #f4f3ff;
  box-shadow: 0 0 0 1px #7367f0;
}
.tt-card--on .tt-card__name { color: #5b4fd6; }

.tt-card--out { border-style: dashed; }
.tt-card--out .tt-card__name { color: #b9b9c3; }
.tt-card--out .tt-card__avail { color: #ea5455; }
.tt-card--out.tt-card--on {
  border-style: solid;
  border-color: #ff9f43;
  background: #fff6ec;
  box-shadow: 0 0 0 1px #ff9f43;
}
.tt-card--out.tt-card--on .tt-card__name { color: #b06a1f; }

.tt-loading { display: flex; align-items: center; font-size: 12px; color: #b9b9c3; padding: 6px 0; }

.tt-alert--info { background: #eceffd; color: #4c5eb5; }
.tt-alert--info strong { color: #3a4a99; }

.dark-layout .tt-card { background: #283046; border-color: #3b4253; }
.dark-layout .tt-card:hover { border-color: #56607d; }
.dark-layout .tt-card__name { color: #d0d2d6; }
.dark-layout .tt-card--on { background: #2b2a4e; border-color: #7367f0; }
.dark-layout .tt-card--on .tt-card__name { color: #b3aaff; }
.dark-layout .tt-card--out .tt-card__name { color: #676d7d; }
.dark-layout .tt-card--out.tt-card--on { background: #3d2f1c; border-color: #ff9f43; }
.dark-layout .tt-card--out.tt-card--on .tt-card__name { color: #ffbb7a; }
.dark-layout .tt-alert--info { background: #1f2547; color: #a3b0ff; }
.dark-layout .tt-alert--info strong { color: #c5cdff; }

.dec-log-item__state {
  display: inline-flex;
  align-items: center;
  margin-left: 7px;
  padding: 1px 7px;
  border-radius: 9px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  vertical-align: 1px;
}
.dec-log-item__state--pend   { background: #fff3e6; color: #ff9f43; }
.dec-log-item__state--ok     { background: #e6f9ef; color: #28c76f; }
.dec-log-item__state--cancel { background: #f2f2f4; color: #b9b9c3; }

.dark-layout .dec-log-item__state--pend   { background: #3d2f1c; }
.dark-layout .dec-log-item__state--ok     { background: #173d2b; }
.dark-layout .dec-log-item__state--cancel { background: #2a3042; color: #676d7d; }

.dec-log-item__action { flex: 0 0 auto; align-self: center; margin-left: 10px; }
</style>
